import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

import { sendGTMEvent } from '@/fo/api/gtm';
import {
  getFragmentData,
  ProductFragment,
  SimpleProductFragmentDoc
} from '@/fo/api/magento';
import { Option } from '@/fo/ui/kit/listbox/base';
import { useForm } from '@/fo/ui/mod/form/base';
import { utilMapProductData } from '@/fo/util/map-product-data';

import {
  AboutProductSection,
  AboutUserSection,
  FormFooter,
  SuccessMessage,
  PhotoField,
  RecommendedField,
  RatingField,
  RatingValue,
  UnauthorizedMessage
} from '../../components';
import { UserInfo } from '../../types';

import { formAction } from './formAction';
import { styles } from './ReviewForm.styles';

export interface ReviewFormProps {
  skinTypes: Option[];
  skinProblems: Option[];
  ratingId: string;
  ratings: RatingValue[];
  sku: string;
  mindboxProductId: string;
  onSuccess?: () => void;
  userInfo?: UserInfo;
  onClose?: () => void;
  isAuthorized?: boolean;
}

export function ReviewForm({
  skinTypes,
  skinProblems,
  ratingId,
  ratings,
  sku,
  mindboxProductId,
  onSuccess,
  userInfo,
  onClose,
  isAuthorized
}: ReviewFormProps) {
  const slots = styles();

  const t = useTranslations();

  const { getFieldProps, pending, isDirty, state, action } =
    useForm(formAction);

  const [photoLoading, setPhotoLoading] = useState(false);

  const isSuccess = state.status === 'success';
  const submitTrackedRef = useRef(false);

  const products = state.data?.products as ProductFragment[];

  const mappedProducts = products?.map((product) => {
    const productFragment = getFragmentData(SimpleProductFragmentDoc, product);

    return utilMapProductData(productFragment as ProductFragment, t);
  });

  useEffect(() => {
    if (!isSuccess || submitTrackedRef.current) return;

    submitTrackedRef.current = true;
    sendGTMEvent({
      event: 'submit_review',
      eventCategory: 'form',
      eventAction: 'submit'
    });
    onSuccess?.();
  }, [isSuccess, onSuccess]);

  if (!isAuthorized) {
    return <UnauthorizedMessage />;
  }

  if (isSuccess) {
    return (
      <SuccessMessage
        type="review"
        onClick={onClose}
        products={mappedProducts}
      />
    );
  }

  return (
    <form
      className={slots.base()}
      noValidate
      action={action}
    >
      <input
        type="hidden"
        name="sku"
        value={sku}
      />
      <input
        type="hidden"
        name="mindboxProductId"
        value={mindboxProductId}
      />
      <div className={slots.formContainer()}>
        <RatingField
          ratings={ratings}
          ratingId={ratingId}
          {...getFieldProps('rating')}
        />

        <div>
          <h3 className={slots.formTitle()}>
            {t('review.form.aboutUser.title')}
          </h3>
          <AboutUserSection
            skinTypes={skinTypes}
            skinProblems={skinProblems}
            getFieldProps={getFieldProps}
            userInfo={userInfo}
          />
        </div>

        <div>
          <h3 className={slots.formTitle()}>
            {t('review.form.aboutProduct.title')}
          </h3>
          <AboutProductSection getFieldProps={getFieldProps} />
        </div>

        <div>
          <h3 className={slots.formTitle()}>
            {t('review.form.recommended.title')}
          </h3>
          <RecommendedField {...getFieldProps('is_recommended')} />
        </div>

        <div>
          <PhotoField
            {...getFieldProps('images')}
            onLoadingChange={setPhotoLoading}
          />
        </div>
      </div>
      <FormFooter
        type="review"
        pending={pending}
        isDirty={isDirty}
        photoLoading={photoLoading}
        email={userInfo?.email}
        isSubscribed={userInfo?.is_subscribed}
      />
    </form>
  );
}
